import { Button, Collapse, Popconfirm, Tag, Tooltip, Empty, Badge } from "antd";
import { CalendarOutlined, DeleteOutlined, LockOutlined, PlusOutlined, UnlockOutlined } from "@ant-design/icons";
import PropTypes from "prop-types";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import apiClient from "../../../../helpers/apiClient";
import { useToast } from "../../../../contexts/ToastContext";
import AddBlockModal from "../modals/AddBlockModal";
import CarDetailSection from "../../../cars/components/cardetails/CarDetailSection";
import { formatBlockDates, getBlockReasonLabel, isBlockPast } from "../../../cars/utils/carBlockStatus";

const REASON_COLORS = {
    maintenance: "blue",
    personal_use: "purple",
    accident_repair: "red",
    documentation_issue: "orange",
    other: "default",
    // machine-created by the Turo sync, never chosen by an admin
    external_booking: "cyan",
};

const CARD_CLASS =
    "mb-3 rounded-lg border border-[#f0f0f0] bg-white p-4 transition-colors hover:bg-[#fafafa]";

/**
 * The tag shows the block's current state; the button names what clicking it will do.
 * Past blocks can't affect availability any more, so they get no activate/deactivate action.
 */
function getBlockStatus(block, t) {
    if (isBlockPast(block)) {
        return {
            key: "expired",
            label: t("carDetail.blockStatusExpired"),
            tooltip: t("carDetail.blockStatusExpiredTooltip"),
            className: "!border-gray-200 !bg-gray-100 !text-gray-500",
        };
    }
    if (block.isActive) {
        return {
            key: "active",
            label: t("carDetail.blockStatusActive"),
            className: "!border-green-200 !bg-green-50 !text-green-700",
        };
    }
    return {
        key: "inactive",
        label: t("carDetail.blockStatusInactive"),
        className: "!border-gray-200 !bg-gray-100 !text-gray-600",
    };
}

function BlockCard({ block, onToggleActive, onDelete, actionLoading, t }) {
    const status = getBlockStatus(block, t);

    const isToggling = actionLoading === block.id;
    const isDeleting = actionLoading === `${block.id}_del`;
    const isBusy = isToggling || isDeleting;

    const toggle =
        status.key === "active"
            ? {
                  label: t("carDetail.deactivateBlock"),
                  tooltip: t("carDetail.deactivateTooltip"),
                  title: t("carDetail.deactivateBlockTitle"),
                  description: t("carDetail.deactivateBlockDescription"),
                  icon: <UnlockOutlined />,
                  next: false,
              }
            : {
                  label: t("carDetail.activateBlock"),
                  tooltip: t("carDetail.activateTooltip"),
                  title: t("carDetail.activateBlockTitle"),
                  description: t("carDetail.activateBlockDescription"),
                  icon: <LockOutlined />,
                  next: true,
              };

    return (
        <div
            className={`flex items-start justify-between gap-4 ${CARD_CLASS} ${status.key === "active" ? "opacity-100" : "opacity-70"}`}>
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                <div className="flex flex-wrap items-center gap-2.5">
                    <CalendarOutlined className="shrink-0 text-lg text-[#5d5dff]" />
                    <span className="fontSemi text-[0.95rem] text-[#1f1f1f]">{formatBlockDates(block)}</span>
                    <Tag color={REASON_COLORS[block.reason] || "default"} className="m-0 rounded">
                        {getBlockReasonLabel(block.reason, t)}
                    </Tag>
                    {block.externalRef?.platform === "turo" && (
                        <Tag color="geekblue" className="m-0 rounded">
                            {t("carDetail.blockSyncedFromTuro")}
                        </Tag>
                    )}
                    <Tooltip title={status.tooltip}>
                        <Tag className={`m-0 rounded-full font-medium ${status.className}`}>{status.label}</Tag>
                    </Tooltip>
                </div>
                {block.reasonNote && (
                    <span className="pl-6 text-sm text-[#666]">{block.reasonNote}</span>
                )}
            </div>

            <div className="flex shrink-0 gap-1">
                {status.key !== "expired" && (
                    <Tooltip title={toggle.tooltip}>
                        <Popconfirm
                            title={toggle.title}
                            description={toggle.description}
                            onConfirm={() => onToggleActive(block, toggle.next)}
                            okText={toggle.label}
                            cancelText={t("cancel")}
                            disabled={isBusy}>
                            <Button
                                type={toggle.next ? "default" : "text"}
                                size="small"
                                icon={toggle.icon}
                                loading={isToggling}
                                disabled={!!actionLoading && !isToggling}>
                                {toggle.label}
                            </Button>
                        </Popconfirm>
                    </Tooltip>
                )}
                <Tooltip title={t("carDetail.deletePermanently")}>
                    <Popconfirm
                        title={t("carDetail.deleteBlockTitle")}
                        description={t("carDetail.deleteBlockDescription")}
                        onConfirm={() => onDelete(block.id)}
                        okText={t("delete")}
                        okButtonProps={{ danger: true }}
                        cancelText={t("cancel")}
                        disabled={isBusy}>
                        <Button
                            type="text"
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            loading={isDeleting}
                            disabled={!!actionLoading && !isDeleting}
                        />
                    </Popconfirm>
                </Tooltip>
            </div>
        </div>
    );
}

BlockCard.propTypes = {
    block: PropTypes.object.isRequired,
    onToggleActive: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    actionLoading: PropTypes.string,
    t: PropTypes.func.isRequired,
};

function BlockCar({ id, onBlocksChange }) {
    const { t } = useTranslation("cars");
    const toast = useToast();
    const [blocks, setBlocks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);

    const fetchBlocks = useCallback(async () => {
        if (!id) return;
        try {
            setLoading(true);
            const res = await apiClient.get(`/api/car/${id}/blocks`);
            if (res?.data?.success) {
                const fetchedBlocks = res.data.data.blocks || [];
                setBlocks(fetchedBlocks);
                // Lets the car page keep its "car is blocked" warning in step with this list.
                onBlocksChange?.(fetchedBlocks);
            } else {
                toast.error({
                    message: t("carDetail.blockLoadFailed"),
                    description: res?.data?.message || t("carDetail.updateError"),
                });
            }
        } catch (err) {
            toast.error({
                message: t("carDetail.blockLoadFailed"),
                description: err?.response?.data?.message || t("carDetail.updateError"),
            });
        } finally {
            setLoading(false);
        }
    }, [id, toast, t, onBlocksChange]);

    useEffect(() => {
        fetchBlocks();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleToggleActive = async (block, isActive) => {
        const failedMessage = isActive ? t("carDetail.blockActivateFailed") : t("carDetail.blockDeactivateFailed");
        try {
            setActionLoading(block.id);
            const res = await apiClient.patch(`/api/car/${id}/block/${block.id}`, {
                blockType: block.blockType,
                isActive,
            });
            // Only report success if the saved block really changed — the API can answer
            // "updated successfully" while leaving isActive untouched.
            if (res?.data?.data?.block?.isActive === isActive) {
                toast.success({
                    message: isActive
                        ? t("carDetail.blockActivatedSuccess")
                        : t("carDetail.blockDeactivatedSuccess"),
                });
            } else {
                toast.error({ message: failedMessage, description: t("carDetail.blockChangeNotSaved") });
            }
            fetchBlocks();
        } catch (err) {
            toast.error({
                message: failedMessage,
                description: err?.response?.data?.message || t("carDetail.updateError"),
            });
        } finally {
            setActionLoading(null);
        }
    };

    const handleDelete = async (blockId) => {
        try {
            setActionLoading(`${blockId}_del`);
            await apiClient.delete(`/api/car/${id}/block/${blockId}`);
            toast.success({ message: t("carDetail.blockDeletedSuccess") });
            fetchBlocks();
        } catch (err) {
            toast.error({
                message: t("carDetail.blockDeleteFailed"),
                description: err?.response?.data?.message || t("carDetail.updateError"),
            });
        } finally {
            setActionLoading(null);
        }
    };

    // Active means "still capable of blocking a booking", not just the flag
    const activeBlocks = blocks.filter((b) => b.isActive && !isBlockPast(b));
    const inactiveBlocks = blocks.filter((b) => !b.isActive || isBlockPast(b));

    const headerAction = (
        <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setModalOpen(true)}
            className="rounded-md border-[#5d5dff] bg-[#5d5dff]">
            {t("carDetail.addBlock")}
        </Button>
    );

    const titleWithBadge = (
        <div className="flex items-center gap-3">
            <span>{t("carDetail.blockSchedule")}</span>
            {!loading && activeBlocks.length > 0 && (
                <Badge
                    count={activeBlocks.length}
                    className="font-bold shadow-none [&_.ant-badge-count]:border-0 [&_.ant-badge-count]:bg-[#e6e6ff] [&_.ant-badge-count]:text-[#5d5dff]"
                />
            )}
        </div>
    );

    return (
        <>
            <CarDetailSection
                title={titleWithBadge}
                headerAction={headerAction}
                loading={loading}
                empty={!loading && activeBlocks.length === 0 && inactiveBlocks.length === 0}
                emptyDescription={t("carDetail.noActiveBlocks")}
                className="!mt-0"
                embedded
                noPadding>
                {!loading && activeBlocks.length > 0 && (
                    <div className="px-4">
                        {activeBlocks.map((block) => (
                            <BlockCard
                                key={block.id}
                                block={block}
                                onToggleActive={handleToggleActive}
                                onDelete={handleDelete}
                                actionLoading={actionLoading}
                                t={t}
                            />
                        ))}
                    </div>
                )}

                {!loading && activeBlocks.length === 0 && inactiveBlocks.length > 0 && (
                    <div className="p-8">
                        <Empty description={t("carDetail.noActiveBlocks")} image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    </div>
                )}

                {!loading && inactiveBlocks.length > 0 && (
                    <Collapse
                        ghost
                        expandIconPosition="end"
                        className="mt-4 px-4"
                        items={[
                            {
                                key: "inactive",
                                label: (
                                    <span className="text-sm font-medium text-[#888]">
                                        {t("carDetail.inactiveBlocks")} ({inactiveBlocks.length})
                                    </span>
                                ),
                                children: (
                                    <div className="pt-2">
                                        {inactiveBlocks.map((block) => (
                                            <BlockCard
                                                key={block.id}
                                                block={block}
                                                onToggleActive={handleToggleActive}
                                                onDelete={handleDelete}
                                                actionLoading={actionLoading}
                                                t={t}
                                            />
                                        ))}
                                    </div>
                                ),
                            },
                        ]}
                    />
                )}
            </CarDetailSection>

            <AddBlockModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                carId={id}
                onSuccess={fetchBlocks}
            />
        </>
    );
}

BlockCar.propTypes = {
    id: PropTypes.string.isRequired,
    onBlocksChange: PropTypes.func,
};

export default BlockCar;
